"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Assembler = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const values_1 = __importDefault(require("../values"));
const react_bootstrap_1 = require("react-bootstrap");
const State_1 = require("../typeDefs/State");
const Popover_1 = __importDefault(require("react-bootstrap/Popover"));
const react_fontawesome_1 = require("@fortawesome/react-fontawesome");
const free_solid_svg_icons_1 = require("@fortawesome/free-solid-svg-icons");
const smap_1 = require("../smap");
const numberFormatter_1 = require("../numberFormatter");
const ProgressBar_1 = __importDefault(require("react-bootstrap/ProgressBar"));
const Sprite_1 = require("./Sprite");
const bigmath_1 = require("../bigmath");
const useSimulation_1 = require("../hooks/useSimulation");
const useGameState_1 = require("../hooks/useGameState");
require("./Assembler.scss");
function Assembler({ itemName, assemblerName, assemblersMakingThis, state, }) {
    const [lastUpdateTimestamp, setLastUpdateTimestamp] = (0, react_1.useState)(null);
    const [instantAnim, setInstantAnim] = (0, react_1.useState)(false);
    const { dispatchAction } = (0, useGameState_1.useGameState)();
    const progress = (state.productionProgress[itemName] ?? {})[assemblerName] ?? 0n;
    const progressState = (state.productionState[itemName] ?? {})[assemblerName] ?? null;
    const [progressDisplay, setProgressDisplay] = (0, react_1.useState)(progress);
    const knownActualProgress = (0, react_1.useRef)(progress);
    const progressDisplayNumber = (0, bigmath_1.bigToNum)(progressDisplay) / useSimulation_1.PRODUCTION_SCALE;
    const baseCraftTime = values_1.default.timePerRecipe[itemName];
    // const thisPower = state.powerConsumptionProgress[itemName] ?? {};
    const thisPowerState = state.powerConsumptionState[itemName] ?? {};
    const no = assemblersMakingThis[assemblerName] ?? 0n;
    let speedPer = values_1.default.assemblerSpeeds[assemblerName] / baseCraftTime;
    speedPer *= values_1.default.calculateBoost(assemblerName, state);
    const totalSpeed = (0, bigmath_1.scaleBigInt)(no, speedPer);
    let label = ((0, jsx_runtime_1.jsxs)("span", { className: "assembler-count", children: [(0, jsx_runtime_1.jsxs)("span", { className: "assembler-count-name", children: [(0, numberFormatter_1.formatNumber)(no), " ", (0, jsx_runtime_1.jsx)(Sprite_1.Sprite, { name: assemblerName }), values_1.default.displayNames(assemblerName), (0, jsx_runtime_1.jsxs)("span", { className: "rate-per", children: ["(", (0, numberFormatter_1.formatNumber)(speedPer), "/s):"] })] }), " ", "(", (0, numberFormatter_1.formatNumber)(totalSpeed), "/s)"] }));
    let stateDisplay = null;
    const updateSpeed = (0, bigmath_1.bigGt)(totalSpeed, 20) ? 20 : Math.max(4, (0, bigmath_1.bigToNum)(totalSpeed) * 4);
    if (thisPowerState[assemblerName] === State_1.PRODUCTION_NO_POWER) {
        const word = values_1.default.buildingPowerDisplayWord[assemblerName] ?? "Power";
        stateDisplay = ((0, jsx_runtime_1.jsx)(ProgressBar_1.default, { className: "building-progress instant", now: 100, variant: "danger", label: (0, jsx_runtime_1.jsxs)("span", { children: [(0, jsx_runtime_1.jsx)(react_fontawesome_1.FontAwesomeIcon, { icon: free_solid_svg_icons_1.faBolt }), " No ", word] }) }));
    }
    else if (state.disabledRecipes[itemName]) {
        stateDisplay = ((0, jsx_runtime_1.jsx)(ProgressBar_1.default, { className: "building-progress secondary instant", now: 100, label: "Disabled" }));
    }
    else if (progressState === State_1.PRODUCTION_NO_INPUT) {
        stateDisplay = ((0, jsx_runtime_1.jsx)(ProgressBar_1.default, { className: "building-progress instant", now: 100, variant: "danger", label: "Missing Input" }));
    }
    else if (progressState === State_1.PRODUCTION_OUTPUT_BLOCKED) {
        stateDisplay = ((0, jsx_runtime_1.jsx)(ProgressBar_1.default, { className: "building-progress instant", now: 100, variant: "warning", label: "Full" }));
    }
    else if (progress < 0) {
        stateDisplay = ((0, jsx_runtime_1.jsx)(ProgressBar_1.default, { className: "building-progress instant", now: 100, label: "Starting..." }));
    }
    else if (progressState === State_1.PRODUCTION_RUNNING) {
        let speedClass = "slow";
        if (instantAnim)
            speedClass = "instant";
        else if (updateSpeed > 15)
            speedClass = "instant";
        else if (updateSpeed > 8)
            speedClass = "fast";
        stateDisplay = ((0, jsx_runtime_1.jsx)(ProgressBar_1.default, { animated: progressDisplayNumber === 1, className: "building-progress " + speedClass, now: progressDisplayNumber * 100 }));
    }
    (0, react_1.useEffect)(() => {
        if (progressState === State_1.PRODUCTION_RUNNING) {
            if ((0, bigmath_1.bigGt)(totalSpeed, 5)) {
                setProgressDisplay(100n * useSimulation_1.PRODUCTION_SCALE_N);
                return;
            }
            const intervalHandle = setTimeout(() => {
                const now = new Date().getTime();
                if (knownActualProgress.current !== progress) {
                    if (progress < knownActualProgress.current) {
                        setInstantAnim(true);
                        setProgressDisplay(0n);
                    }
                    else {
                        setInstantAnim(false);
                        setProgressDisplay(progress);
                    }
                    knownActualProgress.current = progress;
                }
                else if (lastUpdateTimestamp === null && progress) {
                    setProgressDisplay(progress);
                }
                else {
                    const l = lastUpdateTimestamp ?? now;
                    const timeDelta = (now - l) / 1000;
                    const newProgress = progressDisplay + (0, bigmath_1.scaleBigInt)(totalSpeed, Math.floor(timeDelta * useSimulation_1.PRODUCTION_SCALE));
                    setProgressDisplay(newProgress);
                }
                setLastUpdateTimestamp(now);
            }, 1000 / updateSpeed);
            return () => {
                clearTimeout(intervalHandle);
            };
        }
        else {
            setProgressDisplay(0n);
            setLastUpdateTimestamp(null);
            setInstantAnim(true);
        }
    }, [totalSpeed, progressDisplay, progressState, lastUpdateTimestamp]);
    const powerRequirements = values_1.default.buildingPowerRequirementsPerSecond[assemblerName];
    if ((0, smap_1.keys)(powerRequirements).length > 0) {
        const overlay = ((0, jsx_runtime_1.jsx)(Popover_1.default, { className: "popover-no-max-width", children: (0, jsx_runtime_1.jsxs)(Popover_1.default.Body, { children: [(0, numberFormatter_1.formatNumber)(no), " ", values_1.default.displayNames(assemblerName), " ", "consuming:", (0, jsx_runtime_1.jsx)(react_bootstrap_1.Table, { children: (0, jsx_runtime_1.jsx)("tbody", { children: (0, smap_1.keys)(powerRequirements)
                                .sort()
                                .map((requirement) => {
                                const rate = powerRequirements[requirement] ?? 0n;
                                return ((0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsxs)("td", { children: [(0, jsx_runtime_1.jsx)(Sprite_1.Sprite, { name: requirement }), values_1.default.displayNames(requirement)] }), (0, jsx_runtime_1.jsx)("td", { children: (0, numberFormatter_1.formatNumber)(state.amountThatWeHave[requirement]) }), (0, jsx_runtime_1.jsxs)("td", { className: "rate-per", children: ["(", (0, numberFormatter_1.formatNumber)(rate), "/s)"] }), (0, jsx_runtime_1.jsxs)("td", { children: [(0, numberFormatter_1.formatNumber)((0, bigmath_1.bigMul)(no, rate)), "/s"] })] }, requirement));
                            }) }) })] }) }));
        label = ((0, jsx_runtime_1.jsx)(react_bootstrap_1.OverlayTrigger, { placement: "right", overlay: overlay, children: label }));
    }
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("span", { className: "building-label", children: label }), (0, jsx_runtime_1.jsx)("span", { className: 'remove-building-container', children: (0, jsx_runtime_1.jsx)(react_bootstrap_1.OverlayTrigger, { placement: "left", overlay: (0, jsx_runtime_1.jsx)(Popover_1.default, { children: (0, jsx_runtime_1.jsx)(Popover_1.default.Body, { children: "Remove All" }) }), children: (0, jsx_runtime_1.jsx)(react_bootstrap_1.Button, { className: 'assembler-remove-button', onClick: () => dispatchAction({
                            action: 'remove-building',
                            amount: no,
                            building: assemblerName,
                            recipe: itemName,
                        }), variant: 'secondary', children: "-" }) }) }), (0, jsx_runtime_1.jsx)("span", { className: "building-state-display", children: stateDisplay })] }));
}
exports.Assembler = Assembler;
